import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Anchor, ArrowDown, ArrowUp, Sun, Thermometer, Waves, Wind } from "lucide-react";

export function useCoast() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/coast").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 1_200_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const NAVY = "#0a0f1c";
const SEA = "#0b6e99";
const RIP = { low: { bg: "#16a34a", label: "Low rip current risk" }, moderate: { bg: "#f6a21c", label: "Moderate rip current risk" }, high: { bg: "#d90429", label: "HIGH rip current risk" }, unknown: { bg: "#6b7280", label: "Rip current risk n/a" } };

const TurtleIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="currentColor" {...props}>
    <ellipse cx="32" cy="34" rx="20" ry="15" />
    <circle cx="54" cy="30" r="6" />
    <ellipse cx="14" cy="24" rx="7" ry="4" transform="rotate(-30 14 24)" />
    <ellipse cx="14" cy="44" rx="7" ry="4" transform="rotate(30 14 44)" />
    <ellipse cx="44" cy="48" rx="6" ry="3.5" transform="rotate(30 44 48)" />
    <ellipse cx="44" cy="20" rx="6" ry="3.5" transform="rotate(-30 44 20)" />
  </svg>
);

function BeachCard({ b }) {
  const rip = RIP[b.rip_level] || RIP.unknown;
  return (
    <div className="flex flex-col overflow-hidden rounded-[6px] bg-white shadow-[0_12px_30px_rgba(10,15,28,0.14)]" data-testid={`beach-card-${b.id}`}>
      <div className="flex items-center justify-between px-5 py-3 text-white" style={{ background: NAVY }}>
        <div>
          <div className="font-oswald text-[17px] font-bold uppercase tracking-[0.3em] text-white/60">{b.area}</div>
          <div className="font-oswald text-[38px] font-black uppercase leading-none tracking-wide" data-testid={`beach-name-${b.id}`}>{b.name}</div>
        </div>
        <div className="flex items-center gap-2 rounded-[3px] px-3 py-1.5 font-oswald text-[19px] font-black uppercase tracking-wider" style={{ background: rip.bg }} data-testid={`beach-rip-${b.id}`}><Waves className="h-5 w-5" /> {rip.label}</div>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-4 px-5 py-4">
        <div>
          <div className="font-oswald text-[17px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">Today's tides</div>
          <div className="mt-2 grid grid-cols-1 gap-y-1.5" data-testid={`beach-tides-${b.id}`}>
            {b.tides.slice(0, 4).map((t, i) => (
              <div key={i} className="flex items-center gap-3 whitespace-nowrap font-oswald text-[26px] font-bold uppercase text-[#0a0f1c]">
                {t.type === "High" ? <ArrowUp className="h-5 w-5" style={{ color: SEA }} /> : <ArrowDown className="h-5 w-5 text-[#5b6675]" />}
                <span className={t.type === "High" ? "" : "text-[#5b6675]"}>{t.type}</span>
                <span className="ml-auto tabular-nums">{t.time}</span>
                <span className="w-[78px] text-right text-[22px] text-[#5b6675]">{t.ft} ft</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex w-[190px] flex-col items-center justify-center rounded-[4px] px-3 py-2" style={{ background: "#e8f3f8" }}>
          <Thermometer className="h-7 w-7" style={{ color: SEA }} />
          <div className="font-oswald text-[66px] font-black leading-none text-[#0a0f1c]" data-testid={`beach-temp-${b.id}`}>{b.water_temp ?? "—"}°</div>
          <div className="font-oswald text-[17px] font-bold uppercase tracking-[0.25em] text-[#5b6675]">Water temp</div>
        </div>
      </div>
      <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-[#0a0f1c]/10 px-5 py-3 font-oswald text-[23px] font-bold uppercase tracking-wider text-[#0a0f1c]">
        {b.surf && <span className="flex items-center gap-1.5"><Waves className="h-4 w-4" style={{ color: SEA }} /> Surf {b.surf}</span>}
        {b.uv && <span className="flex items-center gap-1.5"><Sun className="h-4 w-4 text-[#f6a21c]" /> UV {b.uv}</span>}
        {b.high && <span className="flex items-center gap-1.5 text-[#5b6675]">High {b.high}</span>}
      </div>
    </div>
  );
}

export function CoastSlide({ coast }) {
  const beaches = coast?.beaches || [];
  if (!beaches.some((b) => b.tides?.length)) return null;
  const turtles = coast.turtles || {};
  return (
    <div data-testid="coast-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(160deg, #eef5f9 0%, #cfe2ec 60%, #b9d4e3 100%)" }}>
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)" }} />
      <div className="relative flex h-full w-full flex-col px-[48px] pb-[165px] pt-[6px]">
        <div className="mb-3 flex items-center justify-between font-oswald text-[19px] font-bold uppercase tracking-[0.2em] text-[#355067]">
          <span className="flex items-center gap-2"><Anchor className="h-5 w-5" style={{ color: SEA }} /> Tybee · St. Simons · Jekyll — tides, water temp, rip current risk</span>
          <span>NOAA Tides &amp; Currents · NWS Charleston / Jacksonville surf-zone forecast</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {beaches.map((b) => <BeachCard key={b.id} b={b} />)}
        </div>
        <div className="mt-4 grid flex-1 grid-cols-[1fr_1fr_470px] gap-4">
          {(coast.marine || []).slice(0, 2).map((m) => (
            <div key={m.zone} className="flex flex-col justify-center rounded-[6px] bg-white/90 px-6 py-4 shadow-[0_8px_24px_rgba(10,15,28,0.1)]" data-testid={`marine-${m.zone}`}>
              <div className="flex items-center gap-2 font-oswald text-[17px] font-bold uppercase tracking-[0.3em]" style={{ color: SEA }}><Wind className="h-5 w-5" /> Marine · {m.label} · {m.zone}</div>
              {m.periods.slice(0, 2).map((p) => (
                <div key={p.name} className="mt-2 text-[26px] leading-snug text-[#0a0f1c]"><span className="font-oswald font-black uppercase">{p.name}: </span>{p.text.replace(/\s*Wave Detail:.*$/i, "")}</div>
              ))}
            </div>
          ))}
          <div className="flex items-center gap-5 rounded-[6px] px-6 py-4 text-white shadow-[0_8px_24px_rgba(10,15,28,0.18)]" style={{ background: turtles.tone === "active" ? "#1b7f5e" : "#3b4a5a" }} data-testid="turtle-season">
            <TurtleIcon className="h-24 w-24 shrink-0 text-white/90" />
            <div>
              <div className="font-oswald text-[17px] font-bold uppercase tracking-[0.3em] text-white/70">Sea turtle watch</div>
              <div className="font-oswald text-[34px] font-black uppercase leading-none">{turtles.phase}</div>
              <div className="mt-2 text-[25px] leading-snug text-white/90">{turtles.text}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
